/*
 * BodyStop.in - Free gift above a cart value (Minimog theme)
 * Adds Pet Saffa Classic Masala Laxative Green Tea to the cart when the
 * qualifying cart value reaches the threshold, and removes it if the cart drops below.
 * After the end date it stops adding the tea and takes out any it added earlier.
 *
 * Works with the Buy X Get Y automatic discount in Shopify admin, which is
 * what makes the tea Rs. 0. Keep the threshold and end date here the same as
 * in the discount.
 *
 * How it works: it never changes the theme's own cart requests. It notices when
 * the cart changes, fixes the tea line, then redraws the mini cart and cart page
 * with Minimog's own functions (onCartDrawerUpdate / onCartUpdate). Only on the
 * cart page, if the page still doesn't match after that, it reloads once
 * (at most once in 20 s).
 * Shopify tags every qualifying product with a Rs. 0 offer label; the script
 * hides that label on every row except the tea's.
 * Razorpay Buy Now: when the product alone reaches the threshold and the cart is
 * empty, Buy Now puts the product and the tea in the cart and opens Magic Checkout
 * for the cart. This works on product pages and in the quick view popup ("Select
 * options" on a product card). Set buyNowWithTea: false to switch this off.
 *
 * Install: Assets > bodystop-free-gift.js, then load it in layout/theme.liquid
 * just before </body>:
 *   <script src="{{ 'bodystop-free-gift.js' | asset_url }}" defer></script>
 */
(() => {
  'use strict';

  const CONFIG = {
    giftVariantId: 67627364581677, // Pet Saffa Laxative Green Tea Classic Masala 25 Tea Bags
    thresholdPaise: 150000,    // Rs. 1,500 (Shopify counts money in paise)
    endsAt: '2026-11-15T16:00:00+05:30', // offer end, India time (null = no end)
    giftProperty: '_bodystop_free_gift', // hidden line property that marks the auto-added tea
    teaHandle: 'pet-saffa-laxative-green-tea-classic-masala-25-tea-bags', // product URL handle
    offerLabel: 'FREE PET SAFFA TEA', // start of the discount title; shown only on the tea row
    buyNowWithTea: true,       // false = leave Razorpay's Buy Now button completely alone
    debug: false,              // true = log steps in the browser console
  };

  if (!CONFIG.giftVariantId || window.__bodystopFreeGift) return;
  window.__bodystopFreeGift = true;

  const nativeFetch = window.fetch.bind(window);
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CART_CALL = /\/cart\/(add|change|update|clear)(\.js)?$/;
  const OWN_MARK = 'bs_gift=1';   // tags this script's own cart calls so it ignores them
  const SETTLE_MS = 150;          // let the theme finish its own cart update first

  const ON_CART_PAGE = /\/cart\/?$/.test(window.location.pathname);

  const KEY_DECLINED = 'bs_gift_declined';   // customer removed the tea on purpose
  const KEY_IN_CART = 'bs_gift_in_cart';     // auto-added tea was in the cart last check
  const KEY_SOLD_OUT = 'bs_gift_sold_out';   // tea could not be added (out of stock)
  const KEY_PAUSED = 'bs_gift_paused_until'; // safety net tripped: only removes, never adds (for 5 min)
  const KEY_CHANGES = 'bs_gift_changes';     // times this script changed the cart
  const KEY_RELOADED = 'bs_gift_reloaded_at';

  const log = (...args) => { if (CONFIG.debug) console.log('[BodyStop gift]', ...args); };

  const store = {
    get(key) { try { return sessionStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) {
      try { value == null ? sessionStorage.removeItem(key) : sessionStorage.setItem(key, String(value)); } catch (e) { /* storage blocked */ }
    },
  };
  const flag = {
    get: (key) => store.get(key) === '1',
    set: (key, on) => store.set(key, on ? '1' : null),
  };
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const pathOf = (url) => { try { return new URL(url, window.location.origin).pathname; } catch (e) { return ''; } };
  const urlOf = (input) => (typeof input === 'string' ? input : (input && input.url) || String(input));
  const isThemeCartCall = (url) => CART_CALL.test(pathOf(url)) && String(url).indexOf(OWN_MARK) === -1;

  // ---------------------------------------------------------------- the offer rules

  const isTea = (item) => item.variant_id === CONFIG.giftVariantId;
  const isAutoGift = (item) => isTea(item) && !!(item.properties && item.properties[CONFIG.giftProperty]);

  // Mirrors Shopify's rule: full price of qualifying items, the tea itself excluded,
  // and items that already got money off from another product discount don't count.
  // The free-tea offer itself tags the qualifying items with a Rs. 0 allocation,
  // so only allocations that actually take money off are treated as another discount.
  const hasRealDiscount = (item) =>
    (item.line_level_discount_allocations || []).some((a) => Number(a.amount) > 0);
  const qualifyingTotal = (cart) => cart.items.reduce((sum, item) => {
    if (isTea(item) || hasRealDiscount(item)) return sum;
    return sum + item.original_line_price;
  }, 0);

  // Safety net: if the tea goes in and out more than 6 times in a minute, something
  // is fighting the script, so it stops adding for 5 minutes (it still takes an
  // auto-added tea out). Normal shopping never gets near this.
  const isPaused = () => Number(store.get(KEY_PAUSED) || 0) > Date.now();
  function noteChange() {
    const now = Date.now();
    let times = [];
    try { times = JSON.parse(store.get(KEY_CHANGES) || '[]'); } catch (e) { /* bad value */ }
    times = times.filter((t) => now - t < 60000).concat(now);
    store.set(KEY_CHANGES, JSON.stringify(times));
    if (times.length > 6) {
      store.set(KEY_PAUSED, now + 5 * 60000);
      store.set(KEY_CHANGES, '[]');
      log('too many changes, paused for 5 minutes');
    }
  }

  let cachedCart = null;   // last cart this script saw (used by Buy Now to decide instantly)
  const getCart = () =>
    nativeFetch(root + 'cart.js?' + OWN_MARK, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => { cachedCart = cart; return cart; });

  const post = (path, body) =>
    nativeFetch(root + path + '?' + OWN_MARK, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

  // Brings the cart in line with the offer. Returns true if the cart changed.
  async function syncGift(cart) {
    if (!cart.items.length) {
      flag.set(KEY_DECLINED, false);
      flag.set(KEY_IN_CART, false);
      return false;
    }

    const offerOn = !isPaused() && (!CONFIG.endsAt || Date.now() < Date.parse(CONFIG.endsAt));
    const qualifies = offerOn && qualifyingTotal(cart) >= CONFIG.thresholdPaise;
    const autoGifts = cart.items.filter(isAutoGift);
    const hasTea = cart.items.some(isTea);

    // The tea was there last time, the cart still qualifies, and now it's gone:
    // the customer removed it, so don't push it back in this session.
    if (flag.get(KEY_IN_CART) && !autoGifts.length && qualifies) flag.set(KEY_DECLINED, true);

    if (qualifies && !hasTea && !flag.get(KEY_DECLINED) && !flag.get(KEY_SOLD_OUT)) {
      const res = await post('cart/add.js', {
        items: [{ id: CONFIG.giftVariantId, quantity: 1, properties: { [CONFIG.giftProperty]: 'Yes' } }],
      });
      if (res.status === 422) flag.set(KEY_SOLD_OUT, true);
      flag.set(KEY_IN_CART, res.ok);
      log(res.ok ? 'tea added' : 'tea could not be added (status ' + res.status + ')');
      return res.ok;
    }

    if (!qualifies && autoGifts.length) {
      let changed = false;
      for (const line of autoGifts) {
        const res = await post('cart/change.js', { id: line.key, quantity: 0 });
        changed = changed || res.ok;
      }
      flag.set(KEY_IN_CART, !changed);
      log('tea removed, cart below threshold');
      return changed;
    }

    flag.set(KEY_IN_CART, autoGifts.length > 0);
    return false;
  }

  // One sync at a time. Resolves to { changed, cart } with the cart as it now stands.
  let chain = Promise.resolve();
  function syncNow() {
    const job = async () => {
      const before = await getCart();
      const changed = await syncGift(before);
      if (changed) noteChange();
      return { changed, cart: changed ? await getCart() : before };
    };
    const result = chain.then(job, job);
    chain = result.catch(() => null);
    return result;
  }

  // ---------------------------------------------------------------- redrawing

  // Redraw using Minimog's own functions (from assets/cart-drawer.js and assets/cart.js):
  //  - mini cart: <m-cart-drawer>.onCartDrawerUpdate() re-renders the items, discounts,
  //    subtotal and cart count, without opening or closing the drawer;
  //  - cart page: <m-cart>.onCartUpdate() re-renders the cart page.
  async function redrawCart() {
    if (typeof document === 'undefined' || !document.querySelector) return;
    try {
      const drawer = document.querySelector('m-cart-drawer');
      if (drawer && typeof drawer.onCartDrawerUpdate === 'function') drawer.onCartDrawerUpdate();
      const cartPage = document.querySelector('m-cart');
      if (cartPage && typeof cartPage.onCartUpdate === 'function') {
        cartPage.onCartUpdate();
        if (typeof cartPage.getCart === 'function' && typeof cartPage.updateCartCount === 'function') {
          cartPage.getCart().then((c) => cartPage.updateCartCount(c.item_count)).catch(() => {});
        }
      }
    } catch (e) {
      log('redraw failed', e);
    }
  }

  // Cart page only: if the page still doesn't match the cart, reload once
  // (never more than once in 20 seconds).
  function pageShowsTea() {
    if (typeof document === 'undefined' || !document.querySelector) return null;
    const scope = document.querySelector('form[action$="/cart"]') || document.querySelector('main') || document.body;
    if (!scope) return null;
    return !!scope.querySelector('a[href*="/products/' + CONFIG.teaHandle + '"]');
  }

  async function checkCartPage() {
    if (!ON_CART_PAGE) return;
    try {
      await sleep(3000);   // give Minimog's own redraw time to land first
      const cart = await getCart();
      const shown = pageShowsTea();
      if (shown === null || shown === cart.items.some(isTea)) return;
      const last = Number(store.get(KEY_RELOADED) || 0);
      if (Date.now() - last < 20000) return;
      store.set(KEY_RELOADED, Date.now());
      log('cart page out of date, reloading once');
      window.location.reload();
    } catch (e) { log(e); }
  }

  // ---------------------------------------------------------------- watching the cart

  // Fetch calls are only watched; the theme's requests and answers pass through untouched.
  window.fetch = function (input, init) {
    const request = nativeFetch(input, init);
    if (isThemeCartCall(urlOf(input))) request.then(() => scheduleSync(), () => {});
    return request;
  };

  let timer = null;
  let quietUntil = 0;

  async function runSync() {
    try {
      const { changed } = await syncNow();
      if (!changed) return;
      quietUntil = Date.now() + 300;
      await redrawCart();
      checkCartPage();
    } catch (e) { log(e); }
  }

  // Changes that arrive while a redraw is settling are checked right after it.
  function scheduleSync() {
    clearTimeout(timer);
    timer = setTimeout(runSync, Math.max(SETTLE_MS, quietUntil - Date.now()));
  }

  // Every network request the page makes (fetch or XHR), however the theme calls it.
  try {
    if (typeof PerformanceObserver === 'function') {
      new PerformanceObserver((list) => {
        if (list.getEntries().some((entry) => isThemeCartCall(entry.name))) scheduleSync();
      }).observe({ type: 'resource' });
    }
  } catch (e) { log('PerformanceObserver unavailable', e); }

  // Minimog's own "cart updated" event (fired after + / − in the drawer or cart page).
  let subscribed = false;
  function subscribeToMinimog() {
    try {
      const events = window.MinimogEvents;
      const name = window.MinimogTheme && window.MinimogTheme.pubSubEvents && window.MinimogTheme.pubSubEvents.cartUpdate;
      if (subscribed || !name || !events || typeof events.subscribe !== 'function') return;
      events.subscribe(name, scheduleSync);
      subscribed = true;
    } catch (e) { /* not available */ }
  }
  subscribeToMinimog();
  if (typeof document !== 'undefined' && document.addEventListener) document.addEventListener('DOMContentLoaded', subscribeToMinimog);

  // ---------------------------------------------------------------- Razorpay Buy Now

  // Razorpay's Buy Now checks out a single product and skips the cart, so the tea can't
  // be added there. When the product alone qualifies and the cart is empty, Buy Now is
  // turned into: put product + tea in the cart, then open Magic Checkout for the cart
  // through the cart drawer's own Check out button. Every other case is left untouched.
  const BUY_NOW = 'magic-checkout-btn[page-type="product"], .rzp-magic-btn__buy-product, ' +
    '.m-product-dynamic-checkout magic-checkout-btn, .m-product-dynamic-checkout .shopify-payment-button__button';
  const ADD_FORM = 'form[action*="/cart/add"]';
  const WAIT = 'wait';   // buyNowPlan: product prices not loaded yet, fetch them first
  const DRAWER_CHECKOUT = 'm-cart-drawer button[name="checkout"], button[name="checkout"][form="cart-drawer-form"]';
  const BUY_NOW_PROP = '_bodystop_buy_now';   // hidden marker on products added by this Buy Now
  const isBuyNowLeftover = (item) => !!(item.properties && item.properties[BUY_NOW_PROP]);
  let productInfo = null;
  let bypassBuyNow = false;
  let buyNowBusy = false;
  const productCache = {};     // product handle -> product JSON (quick view products)
  const productLoading = {};   // product handle -> pending request

  async function loadProductInfo() {
    if (!/\/products\/[^/]+\/?$/.test(window.location.pathname)) return;
    try {
      const res = await nativeFetch(window.location.pathname.replace(/\/$/, '') + '.js?' + OWN_MARK, { headers: { Accept: 'application/json' } });
      if (res.ok) productInfo = await res.json();
    } catch (e) { log('product info unavailable', e); }
  }

  // Prices of any product by its handle (used for the quick view popup).
  function loadProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    if (!productLoading[handle]) {
      productLoading[handle] = nativeFetch(root + 'products/' + encodeURIComponent(handle) + '.js?' + OWN_MARK, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => { if (p) productCache[handle] = p; delete productLoading[handle]; return p; })
        .catch((e) => { delete productLoading[handle]; log('product info unavailable', e); return null; });
    }
    return productLoading[handle];
  }

  // Start loading prices as soon as a quick view popup shows up, so Buy Now can decide instantly.
  function preloadQuickView() {
    if (!CONFIG.buyNowWithTea || typeof document === 'undefined' || !document.querySelectorAll) return;
    document.querySelectorAll('.m-product-quickview form[data-product-handle]').forEach((f) => loadProduct(f.dataset.productHandle));
  }

  // Prices of this product right away from the page itself (Shopify puts them there),
  // so a quick tap after the page opens is never missed.
  function pageProductInfo() {
    const meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
    if (!meta || !Array.isArray(meta.variants)) return null;
    return { id: meta.id, variants: meta.variants.map((v) => ({ id: v.id, price: v.price })) };
  }

  // Cart contents before the script's own first cart check has finished: Minimog's
  // cart-count bubble tells us whether the cart is empty.
  function cartFromBubble() {
    if (typeof document === 'undefined') return null;
    const bubbles = document.querySelectorAll('.m-cart-count-bubble');
    if (!bubbles.length) return null;
    for (const b of bubbles) {
      if (!b.classList.contains('m:hidden') && parseInt(b.textContent, 10) > 0) return null;   // not empty: unknown items
    }
    return { items: [] };
  }

  // The product form this Buy Now belongs to: its own form, else the form in the same
  // quick view popup or product block. Only on a product page may it fall back to the
  // page's main form (elsewhere that could be a different product's card).
  function productForm(btn) {
    const own = btn && btn.closest && btn.closest(ADD_FORM);
    if (own) return own;
    const box = btn && btn.closest && btn.closest('.m-product-quickview, [data-view="quick-view"], .m-main-product--wrapper, product-form');
    const inBox = box && box.querySelector(ADD_FORM);
    if (inBox) return inBox;
    if (!/\/products\/[^/]+\/?$/.test(window.location.pathname)) return null;
    return document.querySelector('main ' + ADD_FORM) || document.querySelector(ADD_FORM);
  }

  // Prices for the product in this form: quick view products by their handle,
  // the product page's own product from the page.
  function productFor(form) {
    const handle = form && form.dataset && form.dataset.productHandle;
    const formId = Number(form && form.dataset && form.dataset.productId) || 0;
    if (handle && productCache[handle]) return productCache[handle];
    const themed = formId && window._themeProducts && window._themeProducts[formId];
    if (themed && Array.isArray(themed.variants)) return themed;
    if (productInfo && (!formId || Number(productInfo.id) === formId)) return productInfo;
    const page = pageProductInfo();
    if (page && (!formId || Number(page.id) === formId)) return page;
    return null;
  }

  // Decide right now (no waiting) whether this Buy Now tap should include the tea.
  function buyNowPlan(btn) {
    if (!CONFIG.buyNowWithTea || isPaused() || flag.get(KEY_SOLD_OUT)) return null;
    if (CONFIG.endsAt && Date.now() >= Date.parse(CONFIG.endsAt)) return null;
    const cart = cachedCart || cartFromBubble();
    if (!cart) return null;
    // Only the tea and leftovers of an earlier, unfinished Buy Now may be in the cart;
    // anything the customer added themselves means Buy Now stays Razorpay's normal one.
    if (cart.items.some((item) => !isAutoGift(item) && !isBuyNowLeftover(item))) return null;

    const form = productForm(btn);
    const product = productFor(form);
    if (!product) return form && form.dataset && form.dataset.productHandle ? WAIT : null;
    const idField = form && form.querySelector('[name="id"]');
    let variantId = Number(idField && idField.value);
    if (!variantId) {
      const fromUrl = new URLSearchParams(window.location.search).get('variant');
      variantId = Number(fromUrl) || ((product.variants || []).length === 1 ? Number(product.variants[0].id) : 0);
    }
    if (!variantId || variantId === CONFIG.giftVariantId) return null;
    const variant = (product.variants || []).find((v) => Number(v.id) === variantId);
    if (!variant || variant.available === false) return null;

    let qtyField = form && form.querySelector('[name="quantity"]');
    if (!qtyField && form && form.id) qtyField = document.querySelector('[name="quantity"][form="' + form.id + '"]');
    const quantity = Math.max(1, parseInt(qtyField && qtyField.value, 10) || 1);

    if (Number(variant.price) * quantity < CONFIG.thresholdPaise) return null;
    return {
      variantId,
      quantity,
      leftoverKeys: cart.items.filter(isBuyNowLeftover).map((item) => item.key),
      hasTea: cart.items.some(isAutoGift),
    };
  }

  const magicCheckoutVisible = () =>
    !!document.querySelector('iframe[src*="razorpay"], .razorpay-container, .razorpay-checkout-frame');

  async function buyNowWithTea(plan, btn) {
    buyNowBusy = true;
    const oldOpacity = btn.style.opacity;
    btn.style.opacity = '0.6';
    try {
      // Clear what an earlier, unfinished Buy Now left behind (real Buy Now never keeps it).
      if (plan.leftoverKeys.length) {
        const updates = {};
        plan.leftoverKeys.forEach((key) => { updates[key] = 0; });
        await post('cart/update.js', { updates });
      }
      const items = [{ id: plan.variantId, quantity: plan.quantity, properties: { [BUY_NOW_PROP]: 'Yes' } }];
      if (!plan.hasTea) {
        items.push({ id: CONFIG.giftVariantId, quantity: 1, properties: { [CONFIG.giftProperty]: 'Yes' } });
      }
      const res = await post('cart/add.js', { items });
      if (!res.ok) throw new Error('add failed (' + res.status + ')');
      flag.set(KEY_IN_CART, true);
      getCart().catch(() => {});
      redrawCart();

      const checkout = document.querySelector(DRAWER_CHECKOUT);
      if (checkout && !checkout.disabled) checkout.click();   // opens Magic Checkout for the cart

      // Safety net: if checkout hasn't opened (and we're still on the page), show the
      // mini cart with the product and the tea so the customer can tap Check out.
      setTimeout(() => {
        if (magicCheckoutVisible()) return;
        const drawer = document.querySelector('m-cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      }, 3000);
    } catch (e) {
      log('Buy Now with tea failed, using normal Buy Now', e);
      normalBuyNow(btn);
    } finally {
      btn.style.opacity = oldOpacity;
      buyNowBusy = false;
    }
  }

  function normalBuyNow(btn) {
    bypassBuyNow = true;
    try { btn.click(); } finally { bypassBuyNow = false; }
  }

  if (typeof window.addEventListener === 'function') {
    window.addEventListener('click', (ev) => {
      if (bypassBuyNow) return;
      const btn = ev.target && ev.target.closest && ev.target.closest(BUY_NOW);
      if (!btn) return;
      if (buyNowBusy) { ev.preventDefault(); ev.stopImmediatePropagation(); return; }
      const plan = buyNowPlan(btn);
      if (!plan) return;                       // normal Razorpay Buy Now
      ev.preventDefault();
      ev.stopImmediatePropagation();
      if (plan !== WAIT) { buyNowWithTea(plan, btn); return; }
      // Quick view product whose prices haven't arrived yet: fetch them, then decide.
      buyNowBusy = true;
      const form = productForm(btn);
      loadProduct(form && form.dataset.productHandle).then(() => {
        buyNowBusy = false;
        const next = buyNowPlan(btn);
        if (next && next !== WAIT) buyNowWithTea(next, btn);
        else normalBuyNow(btn);
      }, () => { buyNowBusy = false; normalBuyNow(btn); });
    }, true);
    // Hovering or pressing Buy Now: start loading the prices early.
    const warm = (ev) => {
      const btn = ev.target && ev.target.closest && ev.target.closest(BUY_NOW);
      const form = btn && productForm(btn);
      if (form && form.dataset && form.dataset.productHandle) loadProduct(form.dataset.productHandle);
    };
    window.addEventListener('pointerover', warm, true);
    window.addEventListener('pointerdown', warm, true);
  }
  loadProductInfo();

  // ---------------------------------------------------------------- offer labels

  // The offer tags every qualifying product with a Rs. 0 line, which the cart drawer and
  // cart page print under each product. Find those labels by their text, inside the cart
  // only, and hide them on every product row except the tea's.
  const HIDDEN_MARK = 'data-bs-gift-hidden';
  const PRODUCT_LINK = 'a[href*="/products/"]';
  const norm = (s) => (s || '').replace(/\s+/g, ' ').trim();
  // What may sit next to the label inside its box: nothing, or a zero amount like "(-Rs. 0.00)".
  const ZERO_ONLY = /^[\s()\-–−]*((rs\.?|₹)\s*0+(\.0+)?)?[\s()\-–−]*$/i;

  const classOf = (n) => (typeof n.className === 'string' ? n.className : (n.getAttribute && n.getAttribute('class')) || '');
  const inCart = (el) => {
    for (let n = el; n && n !== document.body; n = n.parentElement) {
      if (/cart/i.test(n.id || '') || /cart/i.test(classOf(n))) return true;
      if (n.tagName === 'FORM' && /\/cart/.test(n.getAttribute('action') || '')) return true;
    }
    return false;
  };

  // Widen from the text to the whole label (tag icon, amount), never to the price or row.
  function labelBox(textNode) {
    const text = norm(textNode.nodeValue);
    let box = textNode.parentElement;
    while (box.parentElement && box.parentElement !== document.body) {
      const parent = box.parentElement;
      if (parent.querySelector(PRODUCT_LINK)) break;
      if (!ZERO_ONLY.test(norm(parent.textContent).replace(text, ''))) break;
      box = parent;
    }
    return box;
  }

  function tidyOfferLabels() {
    if (typeof document === 'undefined' || !document.body || !document.createTreeWalker) return;
    document.querySelectorAll('[' + HIDDEN_MARK + ']').forEach((el) => {
      el.style.display = '';
      el.removeAttribute(HIDDEN_MARK);
    });
    const walker = document.createTreeWalker(document.body, 4 /* text nodes */);
    const matches = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if ((node.nodeValue || '').toUpperCase().indexOf(CONFIG.offerLabel) !== -1) matches.push(node);
    }
    matches.forEach((node) => {
      const box = labelBox(node);
      if (!inCart(box)) return;
      let row = box.parentElement;
      while (row && row !== document.body && !row.querySelector(PRODUCT_LINK)) row = row.parentElement;
      if (!row || row === document.body) return;
      if (row.querySelector('a[href*="/products/' + CONFIG.teaHandle + '"]')) return;   // the tea's own row
      box.style.display = 'none';
      box.setAttribute(HIDDEN_MARK, '');
    });
  }

  try {
    if (typeof MutationObserver === 'function' && typeof document !== 'undefined' && document.body) {
      let pending = false;
      new MutationObserver(() => {
        if (pending) return;
        pending = true;
        setTimeout(() => { pending = false; tidyOfferLabels(); preloadQuickView(); }, 30);
      }).observe(document.body, { childList: true, subtree: true });
    }
    tidyOfferLabels();
  } catch (e) { log('label tidy unavailable', e); }

  // ---------------------------------------------------------------- page load

  // Check once on page load too (customer returning to an old cart).
  runSync();
})();